import { useEffect, useMemo, useState } from "react";
import { FiFileText, FiUploadCloud, FiX } from "react-icons/fi";
import {
  formatBuildDocumentFileSize,
  uploadBuildDocument,
  validateBuildDocumentFile
} from "../utils/buildDocuments";

const DOCUMENT_TYPE_OPTIONS = ["Warranty", "Other"];

function UploadBuildDocumentModal({
  isOpen,
  onClose,
  ownerId,
  username,
  carId,
  onUploaded,
  maintenanceItems = [],
  mods = []
}) {
  const [title, setTitle] = useState("");
  const [documentType, setDocumentType] = useState("Other");
  const [documentDate, setDocumentDate] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [linkedType, setLinkedType] = useState("");
  const [linkedItemId, setLinkedItemId] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const selectedLinkedItem = useMemo(() => {
    if (linkedType === "maintenance") {
      return maintenanceItems.find(
        (item) => String(item.id) === String(linkedItemId)
      );
    }

    if (linkedType === "mod") {
      return mods.find((mod) => String(mod.id) === String(linkedItemId));
    }

    return null;
  }, [linkedType, linkedItemId, maintenanceItems, mods]);

  const linkedItemTitle = useMemo(() => {
    if (!selectedLinkedItem) return "";

    if (linkedType === "maintenance") {
      return selectedLinkedItem.serviceType || "Maintenance Entry";
    }

    if (linkedType === "mod") {
      return selectedLinkedItem.partName || selectedLinkedItem.brand || "Mod";
    }

    return "";
  }, [linkedType, selectedLinkedItem]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !uploading) {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, uploading]);

  useEffect(() => {
    if (!isOpen) return;

    setTitle("");
    setDocumentType("Other");
    setDocumentDate(getTodayDateValue());
    setDescription("");
    setFile(null);
    setLinkedType("");
    setLinkedItemId("");
    setUploadProgress(0);
    setUploading(false);
    setError("");
    setSuccessMessage("");
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    if (uploading) return;

    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleOverlayMouseDown = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setError("");
    setSuccessMessage("");

    if (selectedFile) {
      try {
        validateBuildDocumentFile(selectedFile);
      } catch (fileError) {
        setError(fileError.message);
      }

      if (!title.trim()) {
        setTitle(getTitleFromFileName(selectedFile.name));
      }
    }
  };

  const handleLinkedTypeChange = (nextLinkedType) => {
    setLinkedType(nextLinkedType);
    setError("");
    setSuccessMessage("");

    if (nextLinkedType === "maintenance" && maintenanceItems.length === 1) {
      setLinkedItemId(String(maintenanceItems[0].id));
      return;
    }

    if (nextLinkedType === "mod" && mods.length === 1) {
      setLinkedItemId(String(mods[0].id));
      return;
    }

    setLinkedItemId("");
  };

  const handleUpload = async () => {
    if (uploading) return;

    setError("");
    setSuccessMessage("");

    if (!title.trim()) {
      setError("Enter a document title.");
      return;
    }

    try {
      validateBuildDocumentFile(file);
    } catch (fileError) {
      setError(fileError.message);
      return;
    }

    if (linkedType === "maintenance" && !linkedItemId) {
      setError("Choose a maintenance entry to link this document to.");
      return;
    }

    if (linkedType === "mod" && !linkedItemId) {
      setError("Choose a mod to link this document to.");
      return;
    }

    if ((linkedType === "maintenance" || linkedType === "mod") && !linkedItemTitle) {
      setError("Linked item title is required.");
      return;
    }

    if (!linkedType && !DOCUMENT_TYPE_OPTIONS.includes(documentType)) {
      setError("Choose a valid document type.");
      return;
    }

    const resolvedCategory =
      linkedType === "maintenance"
        ? "Maintenance"
        : linkedType === "mod"
        ? "Mods"
        : documentType;

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadedDocument = await uploadBuildDocument({
        ownerId,
        username,
        carId,
        title,
        category: resolvedCategory,
        description,
        documentDate,
        file,
        linkedType: linkedType || null,
        linkedItemId,
        linkedItemTitle,
        onProgress: setUploadProgress
      });

      setSuccessMessage("Document added to Build Binder.");

      if (typeof onUploaded === "function") {
        onUploaded(uploadedDocument);
      }

      setTimeout(() => {
        if (typeof onClose === "function") {
          onClose();
        }
      }, 650);
    } catch (uploadError) {
      console.log("Build Binder upload failed:", uploadError);
      setError(
        uploadError?.message ||
          "We could not upload this document. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={handleOverlayMouseDown}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-build-document-title"
        style={modalStyle}
      >
        <header style={headerStyle}>
          <div>
            <div style={eyebrowStyle}>
              <FiUploadCloud aria-hidden="true" />
              Build Binder
            </div>

            <h2 id="upload-build-document-title" style={titleStyle}>
              Add Document
            </h2>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={uploading}
            aria-label="Close upload document modal"
            style={{
              ...closeButtonStyle,
              opacity: uploading ? 0.55 : 1,
              cursor: uploading ? "not-allowed" : "pointer"
            }}
          >
            <FiX aria-hidden="true" />
          </button>
        </header>

        <div style={bodyStyle}>
          <label style={labelStyle}>
            Title
            <input
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
                setError("");
                setSuccessMessage("");
              }}
              placeholder="Example: Oil change receipt"
              disabled={uploading}
              style={inputStyle}
            />
          </label>

          <div style={labelStyle}>
            Link this document

            <div style={radioGroupStyle}>
              <label style={radioLabelStyle}>
                <input
                  type="radio"
                  name="linkedType"
                  checked={linkedType === ""}
                  onChange={() => handleLinkedTypeChange("")}
                  disabled={uploading}
                />
                None
              </label>

              <label style={radioLabelStyle}>
                <input
                  type="radio"
                  name="linkedType"
                  checked={linkedType === "maintenance"}
                  onChange={() => handleLinkedTypeChange("maintenance")}
                  disabled={uploading}
                />
                Maintenance
              </label>

              <label style={radioLabelStyle}>
                <input
                  type="radio"
                  name="linkedType"
                  checked={linkedType === "mod"}
                  onChange={() => handleLinkedTypeChange("mod")}
                  disabled={uploading}
                />
                Mod
              </label>
            </div>
          </div>

          {linkedType === "maintenance" && maintenanceItems.length > 0 && (
            <label style={labelStyle}>
              Maintenance Entry
              <select
                value={linkedItemId}
                onChange={(event) => {
                  setLinkedItemId(event.target.value);
                  setError("");
                  setSuccessMessage("");
                }}
                disabled={uploading}
                style={inputStyle}
              >
                <option value="">Choose maintenance entry</option>

                {maintenanceItems.map((item) => (
                  <option key={String(item.id)} value={String(item.id)}>
                    {getMaintenanceOptionLabel(item)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {linkedType === "maintenance" && maintenanceItems.length === 0 && (
            <div style={linkEmptyStateStyle}>
              <strong style={linkEmptyTitleStyle}>No maintenance entries yet.</strong>
              <span style={linkEmptyTextStyle}>
                Add a maintenance entry before linking a document to it.
              </span>
            </div>
          )}

          {linkedType === "mod" && mods.length > 0 && (
            <label style={labelStyle}>
              Mod
              <select
                value={linkedItemId}
                onChange={(event) => {
                  setLinkedItemId(event.target.value);
                  setError("");
                  setSuccessMessage("");
                }}
                disabled={uploading}
                style={inputStyle}
              >
                <option value="">Choose mod</option>

                {mods.map((mod) => (
                  <option key={String(mod.id)} value={String(mod.id)}>
                    {getModOptionLabel(mod)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {linkedType === "mod" && mods.length === 0 && (
            <div style={linkEmptyStateStyle}>
              <strong style={linkEmptyTitleStyle}>No mods yet.</strong>
              <span style={linkEmptyTextStyle}>
                Add a mod before linking a document to it.
              </span>
            </div>
          )}

          {!linkedType && (
            <label style={labelStyle}>
              Document Type
              <select
                value={documentType}
                onChange={(event) => {
                  setDocumentType(event.target.value);
                  setError("");
                  setSuccessMessage("");
                }}
                disabled={uploading}
                style={inputStyle}
              >
                {DOCUMENT_TYPE_OPTIONS.map((typeOption) => (
                  <option key={typeOption} value={typeOption}>
                    {typeOption}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label style={labelStyle}>
            Document Date
            <input
              type="date"
              value={documentDate}
              onChange={(event) => setDocumentDate(event.target.value)}
              disabled={uploading}
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            Description
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Optional notes about this document"
              disabled={uploading}
              style={textareaStyle}
            />
          </label>

          <label style={fileDropStyle}>
            <FiFileText aria-hidden="true" style={fileIconStyle} />

            <span style={fileDropTitleStyle}>
              {file ? file.name : "Choose PDF, JPG, or PNG"}
            </span>

            <span style={fileDropMetaStyle}>
              {file
                ? formatBuildDocumentFileSize(file.size)
                : "Maximum file size: 10MB"}
            </span>

            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={handleFileChange}
              disabled={uploading}
              style={hiddenFileInputStyle}
            />
          </label>

          {uploading && (
            <div style={progressWrapStyle}>
              <div style={progressTrackStyle}>
                <div
                  style={{
                    ...progressFillStyle,
                    width: `${uploadProgress}%`
                  }}
                />
              </div>

              <p style={progressTextStyle}>{uploadProgress}% uploaded</p>
            </div>
          )}

          {error && (
            <div style={errorStyle} role="alert">
              {error}
            </div>
          )}

          {successMessage && (
            <div style={successStyle} role="status">
              {successMessage}
            </div>
          )}
        </div>

        <footer style={footerStyle}>
          <button
            type="button"
            onClick={handleClose}
            disabled={uploading}
            style={{
              ...cancelButtonStyle,
              opacity: uploading ? 0.65 : 1,
              cursor: uploading ? "not-allowed" : "pointer"
            }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading}
            style={{
              ...uploadButtonStyle,
              opacity: uploading ? 0.7 : 1,
              cursor: uploading ? "not-allowed" : "pointer"
            }}
          >
            {uploading ? "Uploading..." : "Upload Document"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function getMaintenanceOptionLabel(item = {}) {
  const primaryTitle = item.serviceType || "Maintenance Entry";
  const context = [];

  const formattedDate = formatLocalDate(item.serviceDate);
  const formattedMileage = formatMileage(item.mileage);

  if (formattedDate) {
    context.push(formattedDate);
  }

  if (formattedMileage) {
    context.push(formattedMileage);
  }

  return [primaryTitle, ...context].filter(Boolean).join(" • ");
}

function getModOptionLabel(mod = {}) {
  const brand = String(mod.brand || "").trim();
  const partName = String(mod.partName || "").trim();

  let primaryTitle = "Mod";

  if (brand && partName) {
    const partStartsWithBrand = partName
      .toLowerCase()
      .startsWith(brand.toLowerCase());

    primaryTitle = partStartsWithBrand
      ? partName
      : `${brand} • ${partName}`;
  } else if (partName) {
    primaryTitle = partName;
  } else if (brand) {
    primaryTitle = brand;
  }

  const context = [];
  const formattedDate = formatLocalDate(mod.installDate);

  if (formattedDate) {
    context.push(`Installed ${formattedDate}`);
  }

  return [primaryTitle, ...context].filter(Boolean).join(" • ");
}

function formatLocalDate(dateValue) {
  if (!dateValue || typeof dateValue !== "string") {
    return "";
  }

  const parts = dateValue.split("-");

  if (parts.length !== 3) {
    return dateValue;
  }

  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    year < 1000 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return dateValue;
  }

  const localDate = new Date(year, month - 1, day);

  if (
    localDate.getFullYear() !== year ||
    localDate.getMonth() !== month - 1 ||
    localDate.getDate() !== day
  ) {
    return dateValue;
  }

  return localDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function formatMileage(mileage) {
  if (mileage === null || mileage === undefined || mileage === "") {
    return "";
  }

  const numericMileage = Number(String(mileage).replace(/[^0-9.]/g, ""));

  if (!Number.isFinite(numericMileage) || numericMileage <= 0) {
    return "";
  }

  return `${numericMileage.toLocaleString()} mi`;
}

function getTodayDateValue() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function getTitleFromFileName(fileName = "") {
  return String(fileName)
    .replace(/\.[^/.]+$/, "")
    .replace(/[-_]+/g, " ")
    .trim();
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 45000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0,0,0,0.82)",
  padding: "18px",
  boxSizing: "border-box"
};

const modalStyle = {
  width: "100%",
  maxWidth: "520px",
  maxHeight: "calc(100vh - 36px)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#181818",
  border: "1px solid #333",
  borderRadius: "22px",
  color: "#fff",
  boxShadow: "0 24px 70px rgba(0,0,0,0.55)"
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "14px",
  padding: "20px",
  borderBottom: "1px solid #2a2a2a"
};

const eyebrowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "#e10600",
  fontSize: "12px",
  fontWeight: "900",
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  marginBottom: "8px"
};

const titleStyle = {
  margin: 0,
  fontSize: "24px",
  lineHeight: "1.2",
  fontWeight: "850"
};

const closeButtonStyle = {
  width: "40px",
  height: "40px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #333",
  borderRadius: "12px",
  backgroundColor: "#222",
  color: "#fff",
  fontSize: "18px"
};

const bodyStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  padding: "20px",
  overflowY: "auto",
  boxSizing: "border-box"
};

const labelStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "7px",
  color: "#d8d8d8",
  fontSize: "13px",
  fontWeight: "800"
};

const inputStyle = {
  width: "100%",
  minHeight: "44px",
  border: "1px solid #333",
  borderRadius: "12px",
  padding: "11px 12px",
  backgroundColor: "#111",
  color: "#fff",
  fontSize: "16px",
  boxSizing: "border-box"
};

const textareaStyle = {
  ...inputStyle,
  minHeight: "92px",
  resize: "vertical",
  lineHeight: "1.45"
};

const radioGroupStyle = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
  backgroundColor: "#111",
  border: "1px solid #333",
  borderRadius: "12px",
  padding: "12px",
  boxSizing: "border-box"
};

const radioLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "700",
  cursor: "pointer"
};

const linkEmptyStateStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "5px",
  backgroundColor: "#111",
  border: "1px solid #333",
  borderRadius: "12px",
  padding: "12px",
  boxSizing: "border-box"
};

const linkEmptyTitleStyle = {
  color: "#fff",
  fontSize: "14px",
  lineHeight: "1.35"
};

const linkEmptyTextStyle = {
  color: "#aaa",
  fontSize: "13px",
  lineHeight: "1.45"
};

const fileDropStyle = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "140px",
  border: "1px dashed #444",
  borderRadius: "16px",
  backgroundColor: "#111",
  padding: "18px",
  textAlign: "center",
  cursor: "pointer",
  boxSizing: "border-box"
};

const fileIconStyle = {
  color: "#e10600",
  fontSize: "28px"
};

const fileDropTitleStyle = {
  color: "#fff",
  fontSize: "15px",
  fontWeight: "850",
  overflowWrap: "anywhere"
};

const fileDropMetaStyle = {
  color: "#999",
  fontSize: "13px"
};

const hiddenFileInputStyle = {
  position: "absolute",
  inset: 0,
  opacity: 0,
  cursor: "pointer"
};

const progressWrapStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px"
};

const progressTrackStyle = {
  height: "9px",
  borderRadius: "999px",
  backgroundColor: "#2a2a2a",
  overflow: "hidden"
};

const progressFillStyle = {
  height: "100%",
  borderRadius: "999px",
  backgroundColor: "#e10600",
  transition: "width 160ms ease"
};

const progressTextStyle = {
  margin: 0,
  color: "#aaa",
  fontSize: "13px",
  fontWeight: "700"
};

const errorStyle = {
  border: "1px solid rgba(225, 6, 0, 0.45)",
  borderRadius: "12px",
  padding: "12px",
  backgroundColor: "rgba(225, 6, 0, 0.12)",
  color: "#ffd6d6",
  fontSize: "14px",
  lineHeight: "1.45"
};

const successStyle = {
  border: "1px solid rgba(61, 220, 132, 0.35)",
  borderRadius: "12px",
  padding: "12px",
  backgroundColor: "rgba(61, 220, 132, 0.12)",
  color: "#b9ffd6",
  fontSize: "14px",
  lineHeight: "1.45"
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  flexWrap: "wrap",
  padding: "16px 20px",
  borderTop: "1px solid #2a2a2a"
};

const cancelButtonStyle = {
  minHeight: "44px",
  border: "1px solid #3a3a3a",
  borderRadius: "14px",
  padding: "11px 15px",
  backgroundColor: "#242424",
  color: "#fff",
  fontSize: "15px",
  fontWeight: "850"
};

const uploadButtonStyle = {
  minHeight: "44px",
  border: "none",
  borderRadius: "14px",
  padding: "11px 15px",
  backgroundColor: "#e10600",
  color: "#fff",
  fontSize: "15px",
  fontWeight: "850"
};

export default UploadBuildDocumentModal;